import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ABSOLUTE_ROUTES } from 'configs/routes';
import { CARD_ACTIONS_BUTTONS_VALUES, ORDER_CARD_HEIGHT } from './configs';
import { useNotifications } from 'hooks';
import { useLocaleTimeUtils } from 'hooks/useLocaleTimeUtils';
import { DisplayDiffTime, TIME_FORMAT } from 'utils/date';
import { EOrderDeliveryStatus } from 'types/api';
import { ICardActionBtn, IOrderCardProps } from './types';
import { BUTTON_COLOR_SETS, CustomButton, EButtonColorTypes } from 'components/CustomButton';
import CustomDivider from 'components/CustomDivider';
import PaymentStatusTag from 'components/_tags/PaymentStatusTag';
import OrderPrint from 'components/printableComponents/OrderPrint';
import AddressesInfoSection from './AddressesInfoSection';
import CardHeadTitle from './CardHeadTitle';
import DispatcherOrderTimeline from './DispatcherOrderTimeline';
import styles from './OrderCardList.module.css';
import dayjs from 'dayjs';
import { Card, Flex, Space, Typography as T, Tooltip } from 'antd';
import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

export const OrderCard: FC<IOrderCardProps> = memo(({ card, isLoading = false, onCardActionClick }) => {
	const navigate = useNavigate();
	const notificationsController = useNotifications();
	const timeUtils = useLocaleTimeUtils();
	const { t: tCommon } = useTranslation('common');
	const { t: tDispatcher } = useTranslation('dispatcher');

	// ! memos
	const CARD_BUTTONS: ICardActionBtn[] = useMemo(() => {
		const activeButtons = CARD_ACTIONS_BUTTONS_VALUES.filter((actionConfig) => card.actions[actionConfig.key]);

		return activeButtons;
	}, [card.actions]);

	const estimatedTimeArrivalMax = useMemo(
		() => timeUtils.getCurrentTimeZoneDate(dayjs(card.max_customer_delivery_eta_at)).format(TIME_FORMAT),
		[card.max_customer_delivery_eta_at, timeUtils]
	);

	// ! render
	return (
		<Card
			size='small'
			type='inner'
			className='w-100'
			loading={isLoading}
			style={{ borderColor: 'rgb(59, 66, 75, 0.3)', height: ORDER_CARD_HEIGHT }}
			styles={{ body: { padding: '6px 12px', minWidth: 'max-content' } }}
			title={isLoading ? null : <CardHeadTitle card={card} />}
			extra={
				<Flex gap='small'>
					{card.delivery_status === EOrderDeliveryStatus.DISPUTED && (
						<Tooltip title={tDispatcher('disputed_status.tooltip')}>
							<T.Text type='danger'>
								<ExclamationCircleOutlined /> {tDispatcher('disputed_status.warning')}
							</T.Text>
						</Tooltip>
					)}

					<OrderPrint
						onlyIcon
						size='small'
						orderId={card.order_id}
					/>
				</Flex>
			}
		>
			<Flex
				gap='large'
				wrap='nowrap'
				justify='space-between'
			>
				{/* INFO & ACTIONS */}
				<Flex
					gap={6}
					vertical
				>
					{/* MAIN NFO */}
					<Flex
						gap='large'
						wrap='nowrap'
					>
						{/* DETAILS */}
						<Flex
							gap={6}
							vertical
						>
							<Space
								size={4}
								wrap={false}
								split={<CustomDivider type='vertical' />}
							>
								<T.Text>{'#' + card.order_code}</T.Text>

								<Tooltip
									title={tDispatcher('order_card.placed_time', {
										date: timeUtils
											.getCurrentTimeZoneDate(card.order_placed_at)
											.format(TIME_FORMAT),
									})}
								>
									<Flex
										gap={6}
										className={styles.order_date}
									>
										<ClockCircleOutlined />

										<DisplayDiffTime
											type='short'
											startDate={card.order_placed_at}
											endDate={dayjs()}
										/>
									</Flex>
								</Tooltip>

								<T.Text>
									{tDispatcher('order_card.delivery_before', { time: estimatedTimeArrivalMax })}
								</T.Text>
							</Space>

							<Space
								size={4}
								wrap={false}
								split={<CustomDivider type='vertical' />}
							>
								<T.Text>
									{tDispatcher('order_card.total_order')}: {card.total_order}
								</T.Text>

								{card.payment_should_display_payment_method && (
									<Tooltip title={tDispatcher('order_card.order_payment_type')}>
										<Flex gap={4}>
											{card.payment_type_icon}
											{tCommon(`order.payment.types.${card.payment_type}`)}
											<T.Text>{card.to_pay}</T.Text>
										</Flex>
									</Tooltip>
								)}

								<PaymentStatusTag status={card.payment_status} />
							</Space>
						</Flex>

						{/* PICK UP & DELIVERY ADDRESS */}
						<AddressesInfoSection
							pickupAddress={card.pickup_address_latlng}
							deliveryAddress={card.delivery_address_latlng}
						/>
					</Flex>

					{/* ACTIONS BUTTONS */}
					<Flex
						gap='small'
						wrap='nowrap'
					>
						<CustomButton
							size='small'
							type='primary'
							role='button'
							colorTokens={BUTTON_COLOR_SETS[EButtonColorTypes.btn_details]}
							onClick={() => {
								// Stop notification sound
								notificationsController.stopSound();
								navigate(ABSOLUTE_ROUTES.ORDER_DETAILS.replace(':id', card.order_id.toString()));
							}}
						>
							{tDispatcher('order_card.view_details')}
						</CustomButton>

						{CARD_BUTTONS.map(({ key, colorTokens, danger }) => (
							<CustomButton
								key={key}
								size='small'
								role='button'
								type='primary'
								danger={danger}
								colorTokens={colorTokens}
								onClick={() => {
									// Stop notification sound
									notificationsController.stopSound();
									onCardActionClick(card.branch.id, card.order_id, key);
								}}
							>
								{tCommon(`order.status_action_buttons.${key}`)}
							</CustomButton>
						))}
					</Flex>
				</Flex>

				{/* TIMELINE */}
				<DispatcherOrderTimeline timeline={card.timeline} />
			</Flex>
		</Card>
	);
});
